import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import Layout from '../../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import { scopedTransactionSelect, scopedTransactionUpdate } from '../../utils/secureQuery';
import { parseUuid } from '../../utils/inputValidation';
import { Info, AlertCircle } from 'lucide-react';

// --- Función para detectar la moneda automáticamente ---
const getCurrencyByCountry = (countryCode) => {
  const currencies = {
    'CO': 'COP',
    'VE': 'VES',
    'PA': 'USD',
    'US': 'USD',
    'PE': 'PEN',
    'CL': 'CLP'
  };
  return currencies[countryCode] || '';
};

// --- Componente para manejar los logos y sus errores ---
const MethodLogo = ({ methodName, isSelected }) => {
  const [hasError, setHasError] = useState(false);

  // Limpia el nombre para buscar el archivo (ej: "Pago móvil" -> "pago-movil")
  const formattedName = methodName?.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") 
    .replace(/\s+/g, '-');
    
  const imagePath = `/logos/${formattedName}.png`;

  if (hasError) {
    return (
      <div 
        className={`w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center text-2xl font-black shadow-sm border transition-colors
          ${isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-slate-100 text-slate-400 border-slate-200'}`}
      >
        {methodName.charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <div className={`w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center overflow-hidden bg-white shadow-sm border transition-colors
      ${isSelected ? 'border-blue-600' : 'border-slate-200'}
    `}>
      <img
        src={imagePath}
        alt={`Logo de ${methodName}`}
        className="w-full h-full object-cover p-1"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const PaymentStep = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useApp();
  const { transactionId } = location.state || {};

  const [transaction, setTransaction] = useState(null);
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- Cargar transacción ---
  useEffect(() => {
    if (!transactionId || !user?.id) return;

    const fetchTransaction = async () => {
      setLoading(true);
      const { data, error } = await scopedTransactionSelect(supabase, transactionId, user.id).single();

      if (!error) setTransaction(data);
      setLoading(false);
    };

    fetchTransaction();
  }, [transactionId, user?.id]);

  // --- Cargar métodos de pago ACTIVOS según país origen ---
  useEffect(() => {
    if (!transaction?.origin_country) return;

    const fetchMethods = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('payment_methods_origin')
        .select('*')
        .eq('country_code', transaction.origin_country)
        .eq('active', true) 
        .order('price', { ascending: true }); // Ordena primero los más baratos/gratis

      if (!error) setMethods(data || []);
      else setMethods([]);
      setLoading(false);
    };

    fetchMethods();
  }, [transaction]);

  // --- LÓGICA PARA CONTINUAR (ACTUALIZADA CON CÁLCULO DE COMISIÓN) ---
  const handleContinue = async () => {
    if (!selectedMethod) return alert('Selecciona un método de pago');

    setLoading(true);

    // 1. Extraemos el costo de la comisión (si es null o undefined, será 0)
    const fee = Number(selectedMethod.price) || 0;
    
    // 2. Calculamos el Total a Pagar (Monto a enviar + Comisión)
    // Se usa 'amount_origin' de la transacción para el cálculo
    const total = Number(transaction.amount_origin) + fee;

    // 3. Guardamos todo en la base de datos para congelar el recibo
    const { error } = await scopedTransactionUpdate(supabase, transactionId, user.id, {
      payment_method_origin_id: parseUuid(selectedMethod.id, 'Método de pago'),
      payment_fee: fee,
      total_to_pay: total,
      status: 'Método seleccionado',
      current_step: 'amount'
    });

    if (!error) {
      navigate('/send/step3', { state: { transactionId } });
    } else {
      console.error('Error al actualizar método de pago:', error);
      alert('Hubo un problema al procesar tu selección. Intenta de nuevo.');
    }
    
    setLoading(false);
  };

  // --- RENDERIZADO ESTADOS ---
  if (loading && !transaction) {
    return (
      <Layout showNavigation={false}>
        <div className="flex flex-col items-center justify-center min-h-screen text-blue-900 bg-[#f4f7fb]">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
          <p className="text-sm font-black uppercase tracking-widest">Cargando opciones...</p>
        </div>
      </Layout>
    );
  }

  if (!transaction && !loading) {
    return (
      <Layout showNavigation={false}>
        <div className="flex flex-col items-center justify-center min-h-screen text-red-600 bg-[#f4f7fb] px-6 text-center">
          <AlertCircle className="w-16 h-16 mb-4 mx-auto" />
          <p className="text-xl font-bold">Sesión de transacción no encontrada</p>
          <button onClick={() => navigate('/send/step1')} className="mt-4 text-blue-600 font-bold underline">Volver al inicio</button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout showNavigation={false}>
      <div className="min-h-screen bg-[#f4f7fb] flex flex-col pt-6 pb-32 px-5">
        <div className="mx-auto w-full max-w-md flex-1">
          
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-[32px] font-black text-slate-800 tracking-tight leading-tight">
              ¿Cómo deseas pagar?
            </h2>
            <p className="text-slate-500 font-medium mt-2">
              Selecciona el método para enviar tus <span className="text-blue-600 font-bold">{getCurrencyByCountry(transaction.origin_country) || 'fondos'}</span>
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-[32px] shadow-sm border border-slate-100 overflow-hidden p-5 md:p-6"
          >
            {loading ? (
              <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div></div>
            ) : methods.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 bg-slate-50 border border-dashed border-slate-200 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                   <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <p className="text-slate-500 font-black uppercase text-xs tracking-widest">Sin métodos activos</p>
                <p className="text-slate-400 text-sm mt-1 font-medium">Por favor contacta a soporte.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {methods.map((method) => {
                  const isSelected = selectedMethod?.id === method.id;
                  
                  return (
                    <motion.div
                      key={method.id}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedMethod(method)}
                      className={`flex flex-col p-4 rounded-[20px] border-2 transition-all duration-300 cursor-pointer
                        ${isSelected
                          ? 'border-blue-600 bg-blue-50/50 shadow-[0_10px_20px_rgba(37,99,235,0.08)]'
                          : 'border-slate-100 bg-slate-50 hover:bg-white hover:border-slate-200'
                        }`}
                    >
                      {/* --- FILA SUPERIOR: Logo, Nombre, Precio y Checkbox --- */}
                      <div className="flex items-center gap-4">
                        <MethodLogo methodName={method.method} isSelected={isSelected} />
                        
                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                          <span className={`text-[15px] font-black tracking-tight leading-tight truncate transition-colors ${isSelected ? 'text-blue-950' : 'text-slate-800'}`}>
                            {method.method}
                          </span>
                          
                          {/* Precio SIEMPRE visible debajo del nombre */}
                          {method.price !== null && method.price !== undefined && (
                            <div className="mt-1 flex items-center">
                              {Number(method.price) === 0 ? (
                                <span className="inline-flex items-center px-2 py-0.5 bg-emerald-100/80 text-emerald-700 text-[10px] font-black uppercase tracking-widest rounded-md border border-emerald-200/50 shadow-sm">
                                  Gratis
                                </span>
                              ) : (
                                <span className="text-[13px] font-bold text-slate-500">
                                  Costo: {new Intl.NumberFormat('es-CO').format(method.price)} {getCurrencyByCountry(method.country_code)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Checkbox circular */}
                        <div className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                          {isSelected && (
                            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>

                      {/* --- PANEL OCULTO: Comentario Animado --- */}
                      <AnimatePresence>
                        {isSelected && method.comment && (
                          <motion.div
                            initial={{ opacity: 0, height: 0, marginTop: 0 }}
                            animate={{ opacity: 1, height: 'auto', marginTop: 16 }} // Se despliega suavemente
                            exit={{ opacity: 0, height: 0, marginTop: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="px-4 py-3 bg-blue-100/50 rounded-xl border border-blue-200/50 flex items-start gap-2.5">
                              <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                              <span className="text-[11px] font-black uppercase tracking-widest leading-relaxed text-blue-700">
                                {method.comment}
                              </span>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>

        </div>
      </div>

      {/* BOTÓN FLOTANTE ESTILO PREMIUM */}
      <div className="fixed bottom-0 left-0 w-full bg-gradient-to-t from-[#f4f7fb] via-[#f4f7fb] to-transparent pt-12 pb-6 px-5 z-20">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleContinue}
            disabled={loading || !selectedMethod}
            className={`w-full py-5 px-6 rounded-[20px] text-[15px] font-black uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 active:scale-[0.98]
              ${loading || !selectedMethod
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-[0_15px_30px_rgba(37,99,235,0.3)]'
              }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-[3px] border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              'Confirmar Método'
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentStep;

